import { GltfMemoryBudgetExceededError } from '@/plugins/mesh/errors'
import { getAccessorTightByteLength } from '@/plugins/mesh/loaders/gltfLowMemory/DefaultGltfSceneAnalyzer'
import type { GltfBatchPlanner } from '@/plugins/mesh/loaders/gltfLowMemory/interfaces'
import type {
  GltfBatchKind,
  GltfBatchPlan,
  GltfBatchPlanEntry,
  GltfManifest,
  GltfManifestDocument,
  GltfMemoryPlan,
  GltfPlanningContext,
  GltfPrimitiveSource,
  GltfSceneAnalysis,
} from '@/plugins/mesh/loaders/gltfLowMemory/types'

function uniquePrimitiveKey(source: GltfPrimitiveSource): string {
  return `${source.meshIndex}:${source.primitiveIndex}`
}

function estimateUniqueGeometryBytes(manifest: GltfManifest, source: GltfPrimitiveSource): number {
  let total = 0
  for (const accessorIndex of Object.values(source.attributeAccessors)) {
    const accessor = manifest.accessors?.[accessorIndex]
    if (accessor) total += getAccessorTightByteLength(accessor)
  }
  const indexAccessor =
    source.indexAccessor === undefined ? undefined : manifest.accessors?.[source.indexAccessor]
  if (indexAccessor) total += getAccessorTightByteLength(indexAccessor)
  return total
}

function estimateMergedGeometryBytes(
  manifest: GltfManifest,
  sources: readonly GltfPrimitiveSource[]
): number {
  let total = 0
  for (const source of sources) {
    for (const accessorIndex of Object.values(source.attributeAccessors)) {
      const accessor = manifest.accessors?.[accessorIndex]
      if (accessor) total += getAccessorTightByteLength(accessor)
    }
  }
  if (sources[0]?.indexAccessor !== undefined) {
    const totalVertices = sources.reduce((sum, source) => sum + source.vertexCount, 0)
    const totalIndices = sources.reduce((sum, source) => sum + source.indexCount, 0)
    total += totalIndices * (totalVertices > 65_535 ? 4 : 2)
  }
  return total
}

function createEntry(
  kind: GltfBatchKind,
  id: string,
  sources: readonly GltfPrimitiveSource[],
  estimatedGeometryBytes: number,
  spatialCellId: string
): GltfBatchPlanEntry {
  return {
    kind,
    id,
    sources,
    estimatedGeometryBytes,
    spatialCellId,
  }
}

function getMergeCompatibilityKey(source: GltfPrimitiveSource, cellId: string): string {
  return [
    cellId,
    source.renderClass,
    source.materialKey,
    source.vertexLayoutKey,
    source.objectStateKey,
    source.shaderSemanticKey,
    source.indexAccessor === undefined ? 'non-indexed' : 'indexed',
  ].join('|')
}

function computeSpatialCells(
  sources: readonly GltfPrimitiveSource[],
  context: GltfPlanningContext
): ReadonlyMap<GltfPrimitiveSource, string> {
  const cells = new Map<GltfPrimitiveSource, string>()
  if (!context.optimization.partition.enabled) {
    for (const source of sources) cells.set(source, 'global')
    return cells
  }

  let minimumX = Number.POSITIVE_INFINITY
  let minimumY = Number.POSITIVE_INFINITY
  let minimumZ = Number.POSITIVE_INFINITY
  let maximumX = Number.NEGATIVE_INFINITY
  let maximumY = Number.NEGATIVE_INFINITY
  let maximumZ = Number.NEGATIVE_INFINITY
  let largestPrimitiveExtent = 0
  for (const source of sources) {
    if (!source.worldBounds) continue
    minimumX = Math.min(minimumX, source.worldBounds.min.x)
    minimumY = Math.min(minimumY, source.worldBounds.min.y)
    minimumZ = Math.min(minimumZ, source.worldBounds.min.z)
    maximumX = Math.max(maximumX, source.worldBounds.max.x)
    maximumY = Math.max(maximumY, source.worldBounds.max.y)
    maximumZ = Math.max(maximumZ, source.worldBounds.max.z)
    largestPrimitiveExtent = Math.max(
      largestPrimitiveExtent,
      source.worldBounds.max.x - source.worldBounds.min.x,
      source.worldBounds.max.y - source.worldBounds.min.y,
      source.worldBounds.max.z - source.worldBounds.min.z
    )
  }
  const extent = Math.max(maximumX - minimumX, maximumY - minimumY, maximumZ - minimumZ)
  const division = 2 ** Math.min(context.optimization.partition.maxDepth, 10)
  const cellSize =
    Number.isFinite(extent) && extent > 0
      ? Math.max(extent / division, largestPrimitiveExtent * 8, 1)
      : 1

  for (const source of sources) {
    if (!source.worldBounds) {
      cells.set(source, `unsafe:${source.id}`)
      continue
    }
    const centerX = (source.worldBounds.min.x + source.worldBounds.max.x) / 2
    const centerY = (source.worldBounds.min.y + source.worldBounds.max.y) / 2
    const centerZ = (source.worldBounds.min.z + source.worldBounds.max.z) / 2
    cells.set(
      source,
      [
        Math.floor((centerX - minimumX) / cellSize),
        Math.floor((centerY - minimumY) / cellSize),
        Math.floor((centerZ - minimumZ) / cellSize),
      ].join(':')
    )
  }
  return cells
}

function createMemoryPlan(
  document: GltfManifestDocument,
  entries: readonly GltfBatchPlanEntry[],
  context: GltfPlanningContext
): GltfMemoryPlan {
  const sourceBytes = (document.manifest.buffers ?? []).reduce(
    (total, buffer) => total + buffer.byteLength,
    0
  )
  const finalGeometryBytes = entries.reduce(
    (total, entry) => total + entry.estimatedGeometryBytes,
    0
  )
  const largestBatchBytes = entries.reduce(
    (largest, entry) => Math.max(largest, entry.estimatedGeometryBytes),
    0
  )
  const workspaceBytes = (document.manifest.bufferViews ?? []).reduce(
    (largest, bufferView) => Math.max(largest, bufferView.byteLength),
    0
  )
  const peakRangeCacheBytes = context.loading.range.enabled
    ? Math.min(sourceBytes, context.loading.budget.maxRangeCacheBytes)
    : sourceBytes
  const estimatedTransientBytes =
    finalGeometryBytes + workspaceBytes + Math.min(sourceBytes, peakRangeCacheBytes)
  return {
    sourceBytes,
    finalGeometryBytes,
    workspaceBytes,
    estimatedTransientBytes,
    largestBatchBytes,
    actualAllocatedBytes: 0,
    peakRangeCacheBytes,
    additionalDownloadedBytes: 0,
    approvedFinalGeometryBytes: context.loading.budget.maxFinalGeometryBytes,
    approvedTransientBytes: context.loading.budget.maxTransientBytes,
    approvedMergedBatchBytes: context.loading.budget.maxMergedBatchBytes,
    approvedRangeCacheBytes: context.loading.budget.maxRangeCacheBytes,
  }
}

function degradeOversizedMergedEntries(
  document: GltfManifestDocument,
  entries: readonly GltfBatchPlanEntry[],
  context: GltfPlanningContext
): GltfBatchPlanEntry[] {
  const result: GltfBatchPlanEntry[] = []
  for (const entry of entries) {
    if (
      entry.kind !== 'merged' ||
      entry.estimatedGeometryBytes <= context.loading.budget.maxMergedBatchBytes
    ) {
      result.push(entry)
      continue
    }
    const byUniquePrimitive = new Map<string, GltfPrimitiveSource[]>()
    for (const source of entry.sources) {
      const key = uniquePrimitiveKey(source)
      const matches = byUniquePrimitive.get(key) ?? []
      matches.push(source)
      byUniquePrimitive.set(key, matches)
    }
    for (const [key, sources] of byUniquePrimitive) {
      const kind: GltfBatchKind = sources.length > 1 ? 'shared' : 'independent'
      result.push(
        createEntry(
          kind,
          `budget-${kind}:${key}`,
          sources,
          estimateUniqueGeometryBytes(document.manifest, sources[0]),
          'budget-degraded'
        )
      )
    }
  }
  return result
}

function enforceOverallBudget(
  document: GltfManifestDocument,
  memory: GltfMemoryPlan,
  context: GltfPlanningContext
): void {
  if (memory.finalGeometryBytes > context.loading.budget.maxFinalGeometryBytes) {
    throw new GltfMemoryBudgetExceededError(
      document.source,
      'budget-final-exceeded',
      memory.finalGeometryBytes,
      context.loading.budget.maxFinalGeometryBytes
    )
  }
  if (memory.estimatedTransientBytes > context.loading.budget.maxTransientBytes) {
    throw new GltfMemoryBudgetExceededError(
      document.source,
      'budget-transient-exceeded',
      memory.estimatedTransientBytes,
      context.loading.budget.maxTransientBytes
    )
  }
}

/** 元数据级 shared/instanced/merged/independent 执行计划器。 */
export class DefaultGltfBatchPlanner implements GltfBatchPlanner {
  plan(
    document: GltfManifestDocument,
    analysis: GltfSceneAnalysis,
    context: GltfPlanningContext
  ): GltfBatchPlan {
    const { primitives } = analysis
    const entries: GltfBatchPlanEntry[] = []
    const consumed = new Set<GltfPrimitiveSource>()
    const byUniquePrimitive = new Map<string, GltfPrimitiveSource[]>()
    for (const source of primitives) {
      const matches = byUniquePrimitive.get(uniquePrimitiveKey(source)) ?? []
      matches.push(source)
      byUniquePrimitive.set(uniquePrimitiveKey(source), matches)
    }

    if (context.optimization.enabled && context.optimization.instance) {
      for (const [key, matches] of byUniquePrimitive) {
        const safe = matches.filter(
          (source) => source.renderClass !== 'blend' && !source.negativeDeterminant
        )
        if (safe.length < context.optimization.minBatchSize || safe.length !== matches.length) {
          continue
        }
        const geometryBytes = estimateUniqueGeometryBytes(document.manifest, safe[0])
        entries.push(
          createEntry(
            'instanced',
            `instance:${key}`,
            safe,
            geometryBytes + safe.length * 16 * Float32Array.BYTES_PER_ELEMENT,
            'shared'
          )
        )
        for (const source of safe) consumed.add(source)
      }
    }

    const mergeCandidates = primitives.filter(
      (source) =>
        !consumed.has(source) &&
        context.optimization.enabled &&
        context.optimization.merge &&
        source.renderClass !== 'blend' &&
        !source.negativeDeterminant &&
        source.spatialMergeSafe
    )
    const spatialCells = computeSpatialCells(mergeCandidates, context)
    const mergeGroups = new Map<string, GltfPrimitiveSource[]>()
    for (const source of mergeCandidates) {
      const cellId = spatialCells.get(source) ?? `unsafe:${source.id}`
      const key = getMergeCompatibilityKey(source, cellId)
      const matches = mergeGroups.get(key) ?? []
      matches.push(source)
      mergeGroups.set(key, matches)
    }

    for (const group of mergeGroups.values()) {
      const cellId = spatialCells.get(group[0]) ?? `unsafe:${group[0].id}`
      let chunk: GltfPrimitiveSource[] = []
      let chunkVertices = 0
      const flush = () => {
        if (chunk.length >= context.optimization.minBatchSize) {
          const geometryBytes = estimateMergedGeometryBytes(document.manifest, chunk)
          entries.push(
            createEntry('merged', `merge:${entries.length}`, chunk, geometryBytes, cellId)
          )
          for (const source of chunk) consumed.add(source)
        }
        chunk = []
        chunkVertices = 0
      }
      for (const source of group) {
        const exceedsCapacity =
          chunk.length >= context.optimization.partition.maxBatchCapacity ||
          chunkVertices + source.vertexCount > context.optimization.partition.maxVertices
        if (exceedsCapacity) flush()
        chunk.push(source)
        chunkVertices += source.vertexCount
      }
      flush()
    }

    for (const [key, matches] of byUniquePrimitive) {
      const remaining = matches.filter((source) => !consumed.has(source))
      if (remaining.length === 0) continue
      const kind: GltfBatchKind = remaining.length > 1 ? 'shared' : 'independent'
      entries.push(
        createEntry(
          kind,
          `${kind}:${key}`,
          remaining,
          estimateUniqueGeometryBytes(document.manifest, remaining[0]),
          'independent'
        )
      )
    }

    const mergedBatchExceeded = entries.some(
      (entry) =>
        entry.kind === 'merged' &&
        entry.estimatedGeometryBytes > context.loading.budget.maxMergedBatchBytes
    )
    const budgetedEntries = degradeOversizedMergedEntries(document, entries, context)
    const memory = createMemoryPlan(document, budgetedEntries, context)
    enforceOverallBudget(document, memory, context)
    return {
      entries: budgetedEntries,
      memory,
      reasonCode: mergedBatchExceeded ? 'budget-batch-exceeded' : undefined,
      consumedOptimization: {
        shared: true,
        instance: context.optimization.enabled && context.optimization.instance,
        merge: context.optimization.enabled && context.optimization.merge,
        partition:
          context.optimization.enabled &&
          context.optimization.merge &&
          context.optimization.partition.enabled,
        appliedAt: 'loader',
      },
    }
  }
}
